import { Card, Badge, Alert, Modal } from "flowbite-react";
import React, { useMemo } from "react";
import TitleStripe from "../../common/TitleStripe";
import NoOcurrence from "../../common/query/NoOcurrence";
import PendenciasFinanceiras from "../../common/query/PendenciasFinanceiras";
import Protestos from "../../common/query/Protestos";
import HistoricoConsultas from "../../common/query/HistoricoConsultas";
import AvisoCard from "../../common/query/AvisoCard";
import { EvaluationScore } from "../../common/query/EvaluationScore";
import AcoesCiveis from "../../common/query/AcoesCiveis";
import ParticipacaoEmEmpresas from "../../common/query/ParticipacaoEmEmpresas";
import ChequesSustados from "../../common/query/ChequesSustados";
import ChequesSemFundos from "../../common/query/ChequesSemFundos";
import { formatDocument } from "../../../utils/utils";
import ReactApexChart from "react-apexcharts";
import CreditQuadrants from "./CreditQuadrants";
import ScoreRating from "./ScoreRating";
import { HiOutlineEye, HiThumbDown, HiThumbUp } from "react-icons/hi";
import StreetView from "../../common/query/StreetView";
import DecisionBannerPF from "../DecisionBannerPF";
import { FaRegCalendarAlt, FaRegChartBar, FaUserCheck } from "react-icons/fa";
import { IoReceiptOutline } from "react-icons/io5";
import {
  GrDocumentExcel,
  GrDocumentText,
  GrDocumentTime,
} from "react-icons/gr";
import { CiCircleCheck, CiMoneyCheck1 } from "react-icons/ci";
import { MdOutlineBalance } from "react-icons/md";
import { HiOutlineCurrencyDollar } from "react-icons/hi";
import { FiUserCheck, FiUsers } from "react-icons/fi";
import { LuBuilding2, LuMapPin } from "react-icons/lu";
import { IoMdNotificationsOutline } from "react-icons/io";
import { BsBank } from "react-icons/bs";
import moment from "moment";

/** Helpers locais */
const formatCurrency = (val) => {
  if (val === null || val === undefined) return "R$ 0,00";
  try {
    return Number(val).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  } catch {
    const num = Number(val) || 0;
    return num.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  }
};

const safeJoin = (arr) =>
  Array.isArray(arr) ? arr.filter(Boolean).join(", ") : arr || "Indisponível";

const fmt = (v, dash = "0") =>
  v === null || v === undefined || v === "-" ? dash : v;

const Bar = ({ value = 0, max = 1000, title }) => {
  // normaliza para 0..100 (evita overflow se valor > max)
  const pct = Math.max(
    0,
    Math.min(100, Math.round((Number(value) / Number(max || 1)) * 100))
  );
  return (
    <div className="w-full">
      {title && (
        <div className="text-xs text-gray-500 mb-1 flex items-center justify-between">
          <span>{title}</span>
          <span className="font-medium text-gray-700">{fmt(value)}</span>
        </div>
      )}
      <div className="h-2.5 w-full bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-2.5 bg-blue-600 rounded-full transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

const Pill = ({ label, value, color = "info" }) => (
  <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 gap-2">
    <span className="text-xs text-gray-500">{label}</span>
    <Badge color={color}>{fmt(value, "-")}</Badge>
  </div>
);

const StatItem = ({ label, value }) => (
  <div className="flex items-center justify-between px-3 py-2 bg-white rounded-lg border border-gray-200">
    <span className="text-xs text-gray-500">{label}</span>
    <span className="font-semibold text-gray-800">{fmt(value)}</span>
  </div>
);

const TabelaResumoMeses = ({ resumo }) => {
  if (!resumo) return null;
  const linhas = [
    {
      rotulo: "Último mês",
      periodo: resumo?.ultimoMes?.periodo,
      total: resumo?.ultimoMes?.total,
    },
    {
      rotulo: "Penúltimo mês",
      periodo: resumo?.penultimoMes?.periodo,
      total: resumo?.penultimoMes?.total,
    },
    {
      rotulo: "Antepenúltimo mês",
      periodo: resumo?.antepenultimoMes?.periodo,
      total: resumo?.antepenultimoMes?.total,
    },
  ];
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="grid grid-cols-3 bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-600">
        <div>Período</div>
        <div className="text-center">Mês/Ano</div>
        <div className="text-right">Consultas</div>
      </div>
      {linhas.map((l, i) => (
        <div
          key={i}
          className="grid grid-cols-3 px-3 py-2 text-sm border-t border-gray-100"
        >
          <div>{l.rotulo}</div>
          <div className="text-center">{l.periodo || "-"}</div>
          <div className="text-right">{l.total ?? 0}</div>
        </div>
      ))}
      <div className="grid grid-cols-3 px-3 py-2 text-sm border-t border-gray-200 bg-gray-50">
        <div className="font-semibold">Total no período</div>
        <div className="text-center">
          {resumo?.periodoInicial || "-"} → {resumo?.periodoFinal || "-"}
        </div>
        <div className="text-right font-semibold">{resumo?.total ?? 0}</div>
      </div>
    </div>
  );
};

// mapeia a nota A..E para índice, cor e % no trilho
const notaInfo = (notaRaw) => {
  const n = String(notaRaw || "")
    .toUpperCase()
    .trim();
  const mapa = { A: 0, B: 1, C: 2, D: 3, E: 4 };
  const idx = n in mapa ? mapa[n] : null;
  const pct = idx === null ? 0 : (idx / 4) * 100; // 0% A ... 100% E
  const tailwind = ["#16a34a", "#65a30d", "#eab308", "#f59e0b", "#ef4444"]; // fallback

  // cor de badge Flowbite
  const badge =
    idx === 0
      ? "success"
      : idx === 1
      ? "green"
      : idx === 2
      ? "warning"
      : idx === 3
      ? "orange"
      : "failure"; // E

  return {
    idx,
    pct,
    cor: idx === null ? "#6b7280" : tailwind[idx],
    badge,
    nota: n || "-",
  };
};

const TrilhoNota = ({ nota }) => {
  const { pct } = notaInfo(nota);
  return (
    <div className="mt-3 px-2">
      {/* gradiente A→E: verde→vermelho */}
      <div className="h-2.5 w-full rounded-full overflow-hidden bg-gradient-to-r from-green-600 via-yellow-400 to-red-500" />
      {/* marcador */}
      <div className="relative h-0">
        <div
          className="absolute -top-4"
          style={{ left: `calc(${pct}% - 10px)` }}
        >
          <div className="w-5 h-5 rounded-full border-2 border-white shadow ring-2 ring-black/10 bg-white flex items-center justify-center text-[10px] font-bold">
            {String(nota || "-").toUpperCase()}
          </div>
        </div>
      </div>
    </div>
  );
};

const KpiNota = ({ titulo, nota, descricao }) => {
  const { nota: n, badge } = notaInfo(nota);
  return (
    <div className="border border-gray-200 rounded-md shadow-md p-3">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs text-gray-500 mb-1">{titulo}</div>
          <div className="text-3xl font-extrabold leading-tight">{n}</div>
        </div>
        <Badge color={badge} className="text-xs">
          {n === "-" ? "N/D" : `Nota ${n}`}
        </Badge>
      </div>
      <p className="text-xs text-gray-500 mt-1">{descricao}</p>
      <TrilhoNota nota={n} />
    </div>
  );
};

const AcertaCompletoPositivoQuery = ({ data, dataFull }) => {
  const dados = data?.dadosCadastrais || {};
  const [openMap, setOpenMap] = React.useState(false);

  const fullAddress = [
    data?.dadosCadastrais?.endereco,
    data?.dadosCadastrais?.numero,
    data?.dadosCadastrais?.bairro,
    data?.dadosCadastrais?.cidade,
    data?.dadosCadastrais?.cep,
  ]
    .filter(Boolean)
    .join(", ");

  const cpfFormatted = formatDocument(dados?.cpf);
  const birthMoment = dados?.dataNascimento
    ? moment(dados?.dataNascimento, ["DD/MM/YYYY", "YYYY-MM-DD"])
    : null;
  const birthLabel = birthMoment?.isValid()
    ? birthMoment.format("DD/MM/YYYY")
    : null;
  const ageYears =
    birthMoment?.isValid() && birthMoment.isBefore(moment())
      ? moment().diff(birthMoment, "years")
      : null;
  const ageLabel =
    ageYears != null ? `${ageYears} ano${ageYears === 1 ? "" : "s"}` : null;
  const emailValue = safeJoin(dados?.emails || dados?.email);
  const telefoneFixo = safeJoin(dados?.telefoneFixo);
  const celular = safeJoin(dados?.celular || dados?.telefone);

  // Resumo padronizado (padrão acordado)
  const resumo = useMemo(() => {
    const r = data?.resumoConsulta || {};
    return {
      protestos: {
        quantidadeTotal: r?.protestos?.quantidadeTotal ?? 0,
        valorTotal: r?.protestos?.valorTotal ?? 0,
      },
      pendenciasFinanceiras: {
        quantidadeTotal: r?.pendenciasFinanceiras?.quantidadeTotal ?? 0,
        valorTotal: r?.pendenciasFinanceiras?.valorTotal ?? 0,
      },
      acoesCiveis: {
        quantidadeTotal: r?.acoesCiveis?.quantidadeTotal ?? 0,
        valorTotal: r?.acoesCiveis?.valorTotal ?? 0,
      },
      falenciasRecJudiciais: {
        quantidadeTotal: r?.falenciasRecJudiciais?.quantidadeTotal ?? 0,
        ultimaOcorrencia: r?.falenciasRecJudiciais?.ultimaOcorrencia || null,
      },
      chequesSemFundo: {
        quantidadeTotal: r?.chequesSemFundo?.quantidadeTotal ?? 0,
      },
      chequesSustados: {
        quantidadeTotal: r?.chequesSustados?.quantidadeTotal ?? 0,
      },
    };
  }, [data]);

  // Avisos opcionais
  const avisos = Array.isArray(data?.avisos) ? data?.avisos : [];

  const statusConsumidor = data?.statusConsumidor;
  const statusConsumidorContainsNao = statusConsumidor
    ? statusConsumidor.toUpperCase().includes("NÃO") ||
      statusConsumidor.toUpperCase().includes("NAO") ||
      statusConsumidor.toUpperCase().includes("CANCELADA") ||
      statusConsumidor.toUpperCase().includes("SEM")
    : false;

  return (
    <div>
      {/* Opcional:
      <CreditQuadrants
        suggestion="recomendado"
        score={data?.score?.score}
        scoreMax={1000}
        pd={data?.score?.probabilidade}
      />
      */}
      {data?.decisaoExtra?.scoreCodigo ? (
        <DecisionBannerPF
          compact={true}
          flag={data?.decisaoExtra?.scoreCodigo}
        />
      ) : null}

      {/* ===================== DADOS CADASTRAIS ===================== */}
      <TitleStripe className="mb-4">
        <div className="flex items-center gap-2">
          <FiUserCheck className="text-blue-700" />{" "}
          <span>Dados Cadastrais</span>
        </div>
      </TitleStripe>

      <div className="mt-4 text-xs">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
              Nome completo
            </p>
            <p className="text-lg font-semibold text-gray-900">
              {dados?.nome || "Indisponível"}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
              Situação do CPF
            </p>
            <span
              className={`inline-flex items-center rounded-full text-[11px] px-3 py-1 font-semibold ${
                (dados?.situacao || "").toUpperCase() === "REGULAR"
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              {dados?.situacao || "Indisponível"}
            </span>
          </div>
        </div>

        <div className="mt-2 flex flex-wrap gap-2 text-[10px]">
          {cpfFormatted && (
            <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
              CPF: {cpfFormatted}
            </span>
          )}
          {birthLabel && (
            <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
              Data Nasc: {birthLabel}
            </span>
          )}
          {ageLabel && (
            <span className="inline-flex items-center rounded-full border border-gray-200 bg-slate-50 px-2 py-0.5 text-gray-600">
              {ageLabel}
            </span>
          )}
        </div>

        <div className="mt-3 grid gap-2 grid-cols-2">
          {[
            {
              label: "Nome da mãe",
              value: dados?.nomeMae || "Não informado",
              hint: "Parente",
            },
            {
              label: "Origem CPF",
              value: dados?.regiaoCPF || "Não informado",
              hint: "Origem",
            },
            {
              label: "Sexo",
              value: dados?.sexo || "Não informado",
              hint: "Gênero",
            },
            {
              label: "Estado civil",
              value: dados?.estadoCivil || "Não informado",
              hint: "Situação pessoal",
            },
          ].map((item) => (
            <div
              key={item.label}
              className="rounded-xl border border-gray-200 bg-slate-50 px-3 py-2"
            >
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                {item.label}
              </p>
              <p className="text-sm font-semibold text-gray-900">
                {item.value}
              </p>
              <p className="text-[10px] text-gray-500">{item.hint}</p>
            </div>
          ))}
        </div>

        <div className="mt-2 grid gap-2 grid-cols-2">
          {[
            {
              label: "Grau de instrução",
              value: dados?.grauInstrucao || "Não informado",
              hint: "Formação",
            },
            {
              label: "Título de eleitor",
              value: dados?.tituloEleitor || "Não informado",
              hint: "Documento",
            },
          ].map((item) => (
            <div
              key={item.label}
              className="rounded-xl border border-gray-200 bg-slate-50 px-3 py-2"
            >
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                {item.label}
              </p>
              <p className="text-sm font-semibold text-gray-900">
                {item.value}
              </p>
              <p className="text-[10px] text-gray-500">{item.hint}</p>
            </div>
          ))}
        </div>

        <div className="mt-2 grid gap-2 grid-cols-2">
          {[
            {
              label: "Celular",
              value: celular || "Não informado",
            },
            {
              label: "E-mail",
              value: dados?.email || "Não informado",
            },
          ].map((item) => (
            <div
              key={item.label}
              className="rounded-xl border border-gray-200 bg-slate-50 px-3 py-2"
            >
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                {item.label}
              </p>
              <p className="text-sm font-semibold text-gray-900">
                {item.value}
              </p>
              <p className="text-[10px] text-gray-500">{item.hint}</p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <TitleStripe className="mb-4">
          <div className="flex items-center gap-2">
            <LuMapPin className="text-blue-700" /> <span>Localização</span>
          </div>
        </TitleStripe>

        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
              Logradouro
            </p>
            <p className="text-[12px] font-semibold text-gray-900">
              {data?.dadosCadastrais?.endereco || "—"}
            </p>
            {fullAddress && (
              <div
                className="flex items-center gap-1 text-[11px] text-blue-600 cursor-pointer mt-1"
                onClick={() => setOpenMap(true)}
              >
                <span>Ver fotos do local</span>
                <HiOutlineEye className="text-base" />
              </div>
            )}
          </div>
          <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
              Bairro
            </p>
            <p className="text-[12px] font-semibold text-gray-900 flex flex-wrap gap-1">
              <span>{data?.dadosCadastrais?.bairro || "--"}</span>
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
              Cidade
            </p>
            <p className="text-[12px] font-semibold text-gray-900 flex flex-wrap gap-1">
              <span>{data?.dadosCadastrais?.cidade || "--"}</span>
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
              CEP
            </p>
            <p className="text-[12px] font-semibold text-gray-900 flex flex-wrap gap-1">
              <span>{data?.dadosCadastrais?.cep || "--"}</span>
            </p>
          </div>
        </div>
      </div>

      {/* ===================== AVALIAÇÃO / SCORE ===================== */}
      <TitleStripe className="mb-4">
        <div className="flex items-center gap-2">
          <FaRegChartBar className="text-blue-700" />{" "}
          <span>Avaliação preliminar de crédito</span>
        </div>
      </TitleStripe>
      <EvaluationScore
        data={data}
        score={data?.score?.score}
        probability={data?.score?.probabilidade}
        mensagem={data?.score?.mensagem}
      />

      {dataFull?.scoreRating?.statusRetorno == 1 && (
        <ScoreRating score={dataFull?.scoreRating} block={false} />
      )}

      {/* ===================== DECISÃO & LIMITE DE PARCELA ===================== */}
      {data?.limiteParcela && (
        <div>
          <div className="gap-4 border border-gray-100 rounded-md p-3 bg-gray-50 mt-4">
            <div className="text-sm">
              {data?.limiteParcela?.parcelaMensalSugerida ? (
                <>
                  <div className="text-green-500 text-xs font-semibold ">
                    Parcela mensal sugerida
                  </div>
                  <div className="text-2xl font-bold">
                    {formatCurrency(data?.limiteParcela?.parcelaMensalSugerida)}
                  </div>
                </>
              ) : null}

              <div className="mt-1 text-gray-700 text-xs">
                <div>{data?.limiteParcela?.detalhes || "-"}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div>
        {statusConsumidor && (
          <div className="mt-4 w-full">
            <span
              className={`w-full text-sm block px-3 py-5 rounded-lg flex items-center gap-2 ${
                statusConsumidorContainsNao
                  ? "text-red-600 bg-red-50"
                  : "text-emerald-700 bg-emerald-50"
              }`}
            >
              {statusConsumidorContainsNao ? (
                <HiThumbDown className="h-5 w-5" />
              ) : (
                <HiThumbUp className="h-5 w-5" />
              )}
              <span className="font-semibold">{statusConsumidor}</span>
            </span>
          </div>
        )}

        {!statusConsumidorContainsNao && (
          <div>
            <div className="mb-6">
              {data?.painelPositivo?.pontualidadePagamento
                ?.notaFaturaEmAtraso && (
                <div>
                  <TitleStripe className="mb-5">
                    <div className="flex items-center gap-2">
                      <IoReceiptOutline className="text-blue-700" />
                      <span>Faturas em atraso</span>
                    </div>
                  </TitleStripe>
                  <div className="-mt-2 mb-2 text-sm">
                    Indica uma nota para o máximo de dias que 1 fatura ficou em
                    atraso nos últimos 6 meses.
                  </div>

                  <div className="border border-gray-300 grid grid-cols-5 rounded-md overflow-hidden">
                    <div
                      className={`${
                        data?.painelPositivo?.pontualidadePagamento
                          ?.notaFaturaEmAtraso === "A"
                          ? "border-4 border-blue-500"
                          : "border-gray-300 border"
                      } p-2 text-center font-bold text-lg bg-green-200`}
                      style={{ color: "#1B914E" }}
                    >
                      A
                    </div>
                    <div
                      className={`${
                        data?.painelPositivo?.pontualidadePagamento
                          ?.notaFaturaEmAtraso === "B"
                          ? "border-4 border-blue-500"
                          : "border-gray-300 border"
                      } p-2 text-center font-bold text-lg bg-green-100`}
                      style={{ color: "#73CC37" }}
                    >
                      B
                    </div>
                    <div
                      className={`${
                        data?.painelPositivo?.pontualidadePagamento
                          ?.notaFaturaEmAtraso === "C"
                          ? "border-4 border-blue-500"
                          : "border-gray-300 border"
                      } p-2 text-center font-bold text-lg bg-yellow-100`}
                      style={{ color: "#F9C920" }}
                    >
                      C
                    </div>
                    <div
                      className={`${
                        data?.painelPositivo?.pontualidadePagamento
                          ?.notaFaturaEmAtraso === "D"
                          ? "border-4 border-blue-500"
                          : "border-gray-300 border"
                      } p-2 text-center font-bold text-lg bg-orange-100`}
                      style={{ color: "#F4771E" }}
                    >
                      D
                    </div>
                    <div
                      className={`${
                        data?.painelPositivo?.pontualidadePagamento
                          ?.notaFaturaEmAtraso === "E"
                          ? "border-4 border-blue-500"
                          : "border-gray-300 border"
                      } p-2 text-center font-bold text-lg bg-red-100`}
                      style={{ color: "#F23829" }}
                    >
                      E
                    </div>
                  </div>
                  <div className="mt-2 text-sm">
                    Quanto mais próximo o consumidor estiver da faixa{" "}
                    <span className="font-semibold">A</span>, menos dias a
                    fatura está ou ficou em atraso e quanto mais próximo da
                    faixa <span className="font-semibold">E</span>, mais dias a
                    fatura ficou em atraso.
                  </div>
                </div>
              )}

              {data?.painelPositivo?.consumo?.notaContratosRecentes && (
                <div>
                  <TitleStripe className="mb-5">
                    <div className="flex items-center gap-2">
                      <GrDocumentText className="text-blue-700" />
                      <span>Contratos recentes</span>
                    </div>
                  </TitleStripe>
                  <div className="-mt-2 mb-2 text-sm">
                    Indica uma nota para o nível de comprometimento de crédito
                    do consumidor, com base na quantidade de contratos fechados
                    nos últimos 6 meses.
                  </div>

                  <div className="border border-gray-300 grid grid-cols-5 rounded-md overflow-hidden">
                    <div
                      className={`${
                        data?.painelPositivo?.consumo?.notaContratosRecentes ===
                        "A"
                          ? "border-4 border-blue-500"
                          : "border-gray-300 border"
                      } p-2 text-center font-bold text-lg bg-green-200`}
                      style={{ color: "#1B914E" }}
                    >
                      A
                    </div>
                    <div
                      className={`${
                        data?.painelPositivo?.consumo?.notaContratosRecentes ===
                        "B"
                          ? "border-4 border-blue-500"
                          : "border-gray-300 border"
                      } p-2 text-center font-bold text-lg bg-green-100`}
                      style={{ color: "#73CC37" }}
                    >
                      B
                    </div>
                    <div
                      className={`${
                        data?.painelPositivo?.consumo?.notaContratosRecentes ===
                        "C"
                          ? "border-4 border-blue-500"
                          : "border-gray-300 border"
                      } p-2 text-center font-bold text-lg bg-yellow-100`}
                      style={{ color: "#F9C920" }}
                    >
                      C
                    </div>
                    <div
                      className={`${
                        data?.painelPositivo?.consumo?.notaContratosRecentes ===
                        "D"
                          ? "border-4 border-blue-500"
                          : "border-gray-300 border"
                      } p-2 text-center font-bold text-lg bg-orange-100`}
                      style={{ color: "#F4771E" }}
                    >
                      D
                    </div>
                    <div
                      className={`${
                        data?.painelPositivo?.consumo?.notaContratosRecentes ===
                        "E"
                          ? "border-4 border-blue-500"
                          : "border-gray-300 border"
                      } p-2 text-center font-bold text-lg bg-red-100`}
                      style={{ color: "#F23829" }}
                    >
                      E
                    </div>
                  </div>
                  <div className="mt-2 text-sm">
                    Quanto mais próximo da faixa{" "}
                    <span className="font-semibold">A</span>, menos contratos
                    foram fechados e quanto mais perto da faixa{" "}
                    <span className="font-semibold">E</span>, mais contratos
                    foram fechados nos últimos 6 meses.
                  </div>
                </div>
              )}

              {data?.painelPositivo?.pontualidadePagamento
                ?.notaAdiantamento && (
                <div>
                  <TitleStripe className="mb-5">
                    <div className="flex items-center gap-2">
                      <GrDocumentText className="text-blue-700" />
                      <span>Pagamentos adiantados</span>
                    </div>
                  </TitleStripe>
                  <div className="-mt-2 mb-2 text-sm">
                    Indica uma nota para o nível de pagamentos adiantados nos
                    últimos 6 meses.
                  </div>

                  <div className="border border-gray-300 grid grid-cols-5 rounded-md overflow-hidden">
                    <div
                      className={`${
                        data?.painelPositivo?.pontualidadePagamento
                          ?.notaAdiantamento === "A"
                          ? "border-4 border-blue-500"
                          : "border-gray-300 border"
                      } p-2 text-center font-bold text-lg bg-green-200`}
                      style={{ color: "#1B914E" }}
                    >
                      A
                    </div>
                    <div
                      className={`${
                        data?.painelPositivo?.pontualidadePagamento
                          ?.notaAdiantamento === "B"
                          ? "border-4 border-blue-500"
                          : "border-gray-300 border"
                      } p-2 text-center font-bold text-lg bg-green-100`}
                      style={{ color: "#73CC37" }}
                    >
                      B
                    </div>
                    <div
                      className={`${
                        data?.painelPositivo?.pontualidadePagamento
                          ?.notaAdiantamento === "C"
                          ? "border-4 border-blue-500"
                          : "border-gray-300 border"
                      } p-2 text-center font-bold text-lg bg-yellow-100`}
                      style={{ color: "#F9C920" }}
                    >
                      C
                    </div>
                    <div
                      className={`${
                        data?.painelPositivo?.pontualidadePagamento
                          ?.notaAdiantamento === "D"
                          ? "border-4 border-blue-500"
                          : "border-gray-300 border"
                      } p-2 text-center font-bold text-lg bg-orange-100`}
                      style={{ color: "#F4771E" }}
                    >
                      D
                    </div>
                    <div
                      className={`${
                        data?.painelPositivo?.pontualidadePagamento
                          ?.notaAdiantamento === "E"
                          ? "border-4 border-blue-500"
                          : "border-gray-300 border"
                      } p-2 text-center font-bold text-lg bg-red-100`}
                      style={{ color: "#F23829" }}
                    >
                      E
                    </div>
                  </div>
                  <div className="mt-2 text-sm">
                    Quanto mais próximo da faixa{" "}
                    <span className="font-semibold">A</span>, este cliente
                    adianta muitos pagamentos e quanto mais perto da faixa{" "}
                    <span className="font-semibold">E</span>, ele não adianta
                    pagamentos nos últimos 6 meses.
                  </div>
                </div>
              )}
            </div>

            {/* Cards principais */}

            <TitleStripe className="mb-5">
              <div className="flex items-center gap-2">
                <span>Pontualidade de pagamento</span>
              </div>
            </TitleStripe>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="mb-3 ">
                <div className=" mb-2">
                  <div className="font-semibold text-center text-sm">
                    Operações parceladas
                  </div>
                  {/* Pie chart for operações parceladas */}
                  <div className="mb-3">
                    {(() => {
                      const op =
                        data?.painelPositivo?.pontualidadePagamento
                          ?.operacoesParceladas || {};

                      const chartData = [
                        {
                          label: "Pagos em dia",
                          value: Number(op.pagosEmDia || 0),
                        },
                        {
                          label: "Até 5 dias",
                          value: Number(op.pagosAte5Dias || 0),
                        },
                        {
                          label: "6 a 30 dias",
                          value: Number(op.pagos6a30Dias || 0),
                        },
                        {
                          label: "Após 31 dias",
                          value: Number(op.pagosApos31Dias || 0),
                        },
                        {
                          label: "Sem info (5–30)",
                          value: Number(op.semInfo5a30Dias || 0),
                        },
                        {
                          label: "Sem info (+31)",
                          value: Number(op.semInfoApos31Dias || 0),
                        },
                      ].filter((item) => item.value > 0);

                      if (!chartData.length) return null;

                      const series = chartData.map((item) => item.value);
                      const labels = chartData.map((item) => item.label);

                      const options = {
                        labels,
                        legend: {
                          position: "bottom",
                        },
                        dataLabels: {
                          enabled: true,
                          formatter: (val) => `${val.toFixed(1)}%`,
                        },
                        tooltip: {
                          y: {
                            formatter: (val) => `${val}`,
                          },
                        },
                      };

                      return (
                        <ReactApexChart
                          options={options}
                          series={series}
                          type="pie"
                          height={320}
                        />
                      );
                    })()}
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <div className="font-semibold text-center text-sm">
                  Cartão de crédito
                </div>
                {/* Pie chart para cartão de crédito */}
                <div className="mb-3">
                  {(() => {
                    const cc =
                      data?.painelPositivo?.pontualidadePagamento
                        ?.cartaoCredito || {};

                    const chartData = [
                      {
                        label: "Pagos em dia",
                        value: Number(cc.pagosEmDia || 0),
                      },
                      {
                        label: "Até 5 dias",
                        value: Number(cc.pagosAte5Dias || 0),
                      },
                      {
                        label: "6 a 30 dias",
                        value: Number(cc.pagos6a30Dias || 0),
                      },
                      {
                        label: "Após 31 dias",
                        value: Number(cc.pagosApos31Dias || 0),
                      },
                      {
                        label: "Sem info (5–30)",
                        value: Number(cc.semInfo5a30Dias || 0),
                      },
                      {
                        label: "Sem info (+31)",
                        value: Number(cc.semInfoApos31Dias || 0),
                      },
                    ].filter((item) => item.value > 0);

                    if (!chartData.length) return null;

                    const series = chartData.map((item) => item.value);
                    const labels = chartData.map((item) => item.label);

                    const options = {
                      labels,
                      legend: { position: "bottom" },
                      dataLabels: {
                        enabled: true,
                        formatter: (val) => `${val.toFixed(1)}%`,
                      },
                      tooltip: {
                        y: { formatter: (val) => `${val}` },
                      },
                    };

                    return (
                      <ReactApexChart
                        options={options}
                        series={series}
                        type="pie"
                        height={320}
                      />
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ===================== PARTICIPAÇÃO SOCIETÁRIA ===================== */}
      <div>
        <TitleStripe className="mb-5">
          <div className="flex items-center gap-2">
            <LuBuilding2 className="text-blue-700" />
            <span>Participação em empresas</span>
          </div>
        </TitleStripe>
        {Array.isArray(data?.participacaoEmEmpresas) &&
        data?.participacaoEmEmpresas?.length > 0 ? (
          <ParticipacaoEmEmpresas empresas={data?.participacaoEmEmpresas} />
        ) : (
          <NoOcurrence />
        )}
      </div>

      {/* ===================== NEGATIVAÇÕES / PROTESTOS ===================== */}
      <div>
        <TitleStripe className="mb-5">
          <div className="flex items-center gap-2">
            <HiOutlineCurrencyDollar className="text-blue-700" />
            <span>Negativações Registradas</span>
          </div>
        </TitleStripe>
        {Array.isArray(data?.pendenciasFinanceiras) &&
        data?.pendenciasFinanceiras?.length > 0 ? (
          <PendenciasFinanceiras
            quantidadeTotal={resumo.pendenciasFinanceiras.quantidadeTotal}
            valorTotal={resumo.pendenciasFinanceiras.valorTotal}
            pendenciasFinanceiras={data?.pendenciasFinanceiras}
          />
        ) : (
          <NoOcurrence />
        )}

        <div>
          <TitleStripe className="mb-5">
            <div className="flex items-center gap-2">
              <GrDocumentExcel className="text-blue-700" />
              <span>Títulos protestados</span>
            </div>
          </TitleStripe>
          {Array.isArray(data?.protestos) && data?.protestos?.length > 0 ? (
            <Protestos
              quantidadeTotal={resumo.protestos.quantidadeTotal}
              valorTotal={resumo.protestos.valorTotal}
              protestos={data?.protestos}
              temVencimento={false}
            />
          ) : (
            <NoOcurrence />
          )}
        </div>
      </div>

      {/* ===================== CHEQUES ===================== */}
      <div>
        <TitleStripe className="mb-5">
          <div className="flex items-center gap-2">
            <CiMoneyCheck1 className="text-blue-700" />
            <span>Cheques sustados</span>
          </div>
        </TitleStripe>
        {Array.isArray(data?.chequesSustados) &&
        data?.chequesSustados?.length > 0 ? (
          <ChequesSustados chequesSustados={data?.chequesSustados} />
        ) : (
          <NoOcurrence />
        )}
      </div>

      <div>
        <TitleStripe className="mb-5">
          <div className="flex items-center gap-2">
            <CiMoneyCheck1 className="text-blue-700" />
            <span>Cheques sem fundo</span>
          </div>
        </TitleStripe>
        {Array.isArray(data?.chequesSemFundo) &&
        data?.chequesSemFundo?.length > 0 ? (
          <ChequesSemFundos chequesSemFundos={data?.chequesSemFundo} />
        ) : (
          <NoOcurrence />
        )}
      </div>

      {/* ===================== AÇÕES CÍVEIS ===================== */}
      <div>
        <TitleStripe className="mb-5">
          <div className="flex items-center gap-2">
            <MdOutlineBalance className="text-blue-700" />
            <span>Ações Cíveis</span>
          </div>
        </TitleStripe>
        {Array.isArray(data?.acoesCiveis) && data?.acoesCiveis?.length > 0 ? (
          <AcoesCiveis
            quantidadeTotal={resumo.acoesCiveis.quantidadeTotal}
            acoesCiveis={data?.acoesCiveis}
          />
        ) : (
          <NoOcurrence />
        )}
      </div>

      {/* ===================== AVISOS ===================== */}
      {avisos?.length > 0 && (
        <div>
          <TitleStripe className="mb-4">
            <div className="flex items-center gap-2">
              <IoMdNotificationsOutline className="text-blue-700" />
              <span>Avisos</span>
            </div>
          </TitleStripe>
          <div className="grid grid-cols-1 gap-2">
            {avisos.map((a, i) => (
              <AvisoCard
                key={i}
                title={a?.titulo || "Aviso"}
                description={a?.mensagem || a}
              />
            ))}
          </div>
        </div>
      )}

      {/* ===================== RESUMO CONSULTAS ANTERIORES ===================== */}
      <div>
        <TitleStripe className="mb-3">
          <div className="flex items-center gap-2">
            <FaRegCalendarAlt className="text-blue-700" />
            <span>Resumo de consultas (últimos meses)</span>
          </div>
        </TitleStripe>
        {data?.resumoConsultasAnteriores ? (
          <TabelaResumoMeses resumo={data.resumoConsultasAnteriores} />
        ) : (
          <NoOcurrence />
        )}
      </div>

      {/* ===================== HISTÓRICO DE CONSULTAS (lista) ===================== */}
      <div>
        <TitleStripe className="mb-5">
          <div className="flex items-center gap-2">
            <GrDocumentTime className="text-blue-700" />
            <span>Histórico de consultas</span>
          </div>
        </TitleStripe>
        {Array.isArray(data?.historicoConsultas) &&
        data?.historicoConsultas?.length > 0 ? (
          <div className="flex gap-2 flex-col">
            <div className="flex-1">
              <HistoricoConsultas
                historicoConsultas={data?.historicoConsultas}
              />
            </div>
            {/* Gráfico opcional (mantido comentado)
            {data?.historicoConsultasResumo?.paresMesAnoHistoricoConsultas?.length > 0 && (
              <ReactApexChart
                className="border border-gray-200 rounded-md w-full mt-3"
                options={...}
                series={[...]}
                type="bar"
                height={175}
              />
            )} */}
          </div>
        ) : (
          <NoOcurrence />
        )}
      </div>

      <Modal show={openMap} onClose={() => setOpenMap(false)} size="5xl">
        <Modal.Header>Localização</Modal.Header>
        <Modal.Body>
          <div className="space-y-2">
            <div className="text-sm text-gray-600">
              <strong>Endereço:</strong> {fullAddress || "—"}
            </div>
            {fullAddress ? (
              <StreetView address={fullAddress} height={460} />
            ) : (
              <div className="p-3 text-sm text-gray-500">
                Endereço indisponível.
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AcertaCompletoPositivoQuery;
